import type { ReactNode } from 'react';

import isNil from 'lodash/isNil';

import { CategoryBannerRow } from './CategoryBannerRow';
import type { CategoryBanner } from './types';

// `position` counts *visible* categories (0 = before the 1st visible category, N = after the Nth).
// `categoryElements` still holds `null` for rows Games.tsx hides (empty category, jackpot row the
// player can't see), so those are skipped here — otherwise banners either side of a hidden row
// stack back-to-back. Banners positioned past the last visible category render at the end.
export function interleaveCategoryBanners(
  categoryElements: ReactNode[],
  banners: CategoryBanner[] | undefined,
): ReactNode[] {
  if (!banners?.length) return categoryElements;

  // `position` isn't unique — several banners can target the same slot, so keep all of them.
  const bannersByPosition = new Map<number, CategoryBanner[]>();
  banners.forEach((banner) => {
    bannersByPosition.set(banner.position, [...(bannersByPosition.get(banner.position) ?? []), banner]);
  });
  const result: ReactNode[] = [];
  let visibleIdx = 0;

  categoryElements.forEach((element) => {
    if (isNil(element)) return;

    bannersByPosition.get(visibleIdx)?.forEach((banner) => {
      result.push(<CategoryBannerRow key={`category-banner-${banner.id}`} banner={banner} />);
    });
    result.push(element);
    visibleIdx++;
  });

  banners
    .filter((banner) => banner.position >= visibleIdx)
    .sort((a, b) => a.position - b.position)
    .forEach((banner) => {
      result.push(<CategoryBannerRow key={`category-banner-${banner.id}`} banner={banner} />);
    });

  return result;
}
